import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Activity, ArrowDownToLine, Bell, BriefcaseBusiness, CalendarDays, Camera, Eye, Megaphone,
  CheckCircle2, ChevronDown, ChevronLeft, ChevronRight, Clock3, FileText, House, LogOut, MapPin,
  Menu, ShieldCheck, UserRound, Volume2, VolumeX, X,
} from "lucide-react";
import "./staff-dashboard.css";
import "./staff-attendance.css";
import "./staff-profile.css";
import ProfileDataTables from './components/ProfileDataTables';
import './profile-data-tables.css';

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:8000";
const STAFF_SECTION_BY_PATH = {
  "/": "Home",
  "/attendance": "Attendance",
  "/requests": "Requests",
  "/announcements": "Announcements",
  "/profile": "My profile",
};
const STAFF_PATH_BY_SECTION = Object.fromEntries(
  Object.entries(STAFF_SECTION_BY_PATH).map(([path, section]) => [section, path]),
);
const getStaffSectionForPath = (pathname) => STAFF_SECTION_BY_PATH[pathname.replace(/\/+$/, "") || "/"] || null;
const api = async (path, token, options = {}) => {
  const response = await fetch(`${API_URL}${path}`, {
    ...options,
    ...(String(options.method || "GET").toUpperCase() === "GET" ? { cache: "no-store" } : {}),
    headers: { ...(options.body instanceof FormData ? {} : { "Content-Type": "application/json" }), ...(options.headers || {}), Authorization: `Bearer ${token}` },
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(data.detail || "Something went wrong. Please try again.");
  return data;
};
const asDate = (value) => {
  if (!value) return null;
  let timestamp = String(value).trim();
  // The API returns naive datetimes in UTC. Parse them the same way as the
  // manager dashboard so the browser converts them to the employee's timezone.
  if (!/(?:Z|[+-]\d{2}:?\d{2})$/i.test(timestamp)) {
    timestamp = `${timestamp.replace(" ", "T")}Z`;
  }
  const date = new Date(timestamp);
  return Number.isNaN(date.getTime()) ? null : date;
};
const timeLabel = (value) => asDate(value)?.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) || "";
const requestLabel = (request) => request.request_type === "leave" ? "Leave request" : request.request_type === "early_logout" ? "Early logout request" : "Late login request";
const requestSchedule = (request) => request.request_type === "leave"
  ? `${request.from_date || ""} - ${request.to_date || ""}`
  : `${request.permission_date || "Permission date"}${request.expected_departure_time ? ` · Leave by ${request.expected_departure_time}` : request.expected_arrival_time ? ` · Arrive by ${request.expected_arrival_time}` : ""}`;
const announcementMediaItems = (item) => Array.isArray(item?.media) && item.media.length
  ? item.media
  : item?.media_url ? [{ media_url: item.media_url, media_content_type: item.media_content_type, media_filename: item.media_filename }] : [];

function StaffAttendanceSelect({ label, value, onChange, options, placeholder }) {
  const [open, setOpen] = useState(false);
  const selectRef = useRef(null);
  const selectedOption = options.find((option) => String(option.value) === String(value));

  useEffect(() => {
    if (!open) return undefined;
    const closeOnOutsideClick = (event) => {
      if (!selectRef.current?.contains(event.target)) setOpen(false);
    };
    const closeOnEscape = (event) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", closeOnOutsideClick);
    document.addEventListener("keydown", closeOnEscape);
    return () => {
      document.removeEventListener("mousedown", closeOnOutsideClick);
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, [open]);

  return <div className="staff-attendance-select" ref={selectRef}>
    <span className="staff-shift-label-text">{label}</span>
    <button type="button" className={`staff-attendance-select-trigger${open ? " is-open" : ""}`} aria-haspopup="listbox" aria-expanded={open} onClick={() => setOpen((current) => !current)}>
      <span className={selectedOption ? "" : "is-placeholder"}>{selectedOption?.label || placeholder}</span>
      <ChevronDown size={16} aria-hidden="true" />
    </button>
    {open && <div className="staff-attendance-select-options" role="listbox" aria-label={label}>
      {options.length ? options.map((option) => <button type="button" role="option" aria-selected={String(option.value) === String(value)} className={`staff-attendance-select-option${String(option.value) === String(value) ? " is-selected" : ""}`} key={option.value} onClick={() => { onChange(String(option.value)); setOpen(false); }}>
        {option.label}
      </button>) : <div className="staff-attendance-select-empty">No options available</div>}
    </div>}
  </div>;
}

export default function StaffDashboard({ token, employeeId, onLogout, onNavigate }) {
  const [profile, setProfile] = useState(null);
  const [branchName, setBranchName] = useState("");
  const [records, setRecords] = useState([]);
  const [attendanceLoading, setAttendanceLoading] = useState(true);
  const [attendanceMonth, setAttendanceMonth] = useState(() => {
    const today = new Date();
    return `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}`;
  });
  const [attendancePage, setAttendancePage] = useState(0);
  const [profileTab, setProfileTab] = useState("personal");
  const [profilePhotoBusy, setProfilePhotoBusy] = useState(false);
  const [profilePhotoMenuOpen, setProfilePhotoMenuOpen] = useState(false);
  const [profilePhotoPreviewOpen, setProfilePhotoPreviewOpen] = useState(false);
  const [shifts, setShifts] = useState([]);
  const [attendanceBranches, setAttendanceBranches] = useState([]);
  const [attendanceBranchId, setAttendanceBranchId] = useState("");
  const [requests, setRequests] = useState([]);
  const [requestPage, setRequestPage] = useState(0);
  const [announcements, setAnnouncements] = useState([]);
  const [announcementTotal, setAnnouncementTotal] = useState(0);
  const [announcementTotalPages, setAnnouncementTotalPages] = useState(1);
  const [announcementPage, setAnnouncementPage] = useState(0);
  const [announcementReadBusy, setAnnouncementReadBusy] = useState(false);
  const [announcementViewer, setAnnouncementViewer] = useState(null);
  const [announcementSoundEnabled, setAnnouncementSoundEnabled] = useState(() => localStorage.getItem(`hiwaga_announcement_sound_${employeeId}`) !== "false");
  const [section, setSection] = useState(() => {
    return getStaffSectionForPath(window.location.pathname) || "Home";
  });
  const sectionFromHistoryRef = useRef(false);
  const sectionRef = useRef(section);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);
  const [passwordModalOpen, setPasswordModalOpen] = useState(false);
  const [passwordCurrent, setPasswordCurrent] = useState("");
  const [passwordNew, setPasswordNew] = useState("");
  const [passwordConfirm, setPasswordConfirm] = useState("");
  const [passwordBusy, setPasswordBusy] = useState(false);
  const [passwordMessage, setPasswordMessage] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState("");
  const [error, setError] = useState("");
  const [shiftId, setShiftId] = useState("");
  const [requestType, setRequestType] = useState("leave");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [permissionDate, setPermissionDate] = useState("");
  const [arrivalTime, setArrivalTime] = useState("");
  const [departureTime, setDepartureTime] = useState("");
  const [reason, setReason] = useState("");
  const [selfie, setSelfie] = useState(null);
  const [attendanceType, setAttendanceType] = useState("in");
  const [shiftDialogOpen, setShiftDialogOpen] = useState(false);
  const [cameraOpen, setCameraOpen] = useState(false);
  const [selfiePreview, setSelfiePreview] = useState("");
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const profileMenuRef = useRef(null);
  const profilePhotoInputRef = useRef(null);
  const profilePhotoAreaRef = useRef(null);
  const announcementIdsRef = useRef(null);
  const announcementAudioRef = useRef(null);

  const refresh = useCallback(async ({ includeAttendance = false, includeRequests = false } = {}) => {
    if (!employeeId || !token) return;
    setError("");
    const results = await Promise.allSettled([
      api("/auth/me", token),
      Promise.resolve(null),
      includeRequests ? api("/requests/me", token) : Promise.resolve(null),
      api("/attendance/available-branches", token),
    ]);
    if (results[0].status === "fulfilled") {
      const staffProfile = results[0].value;
      setProfile(staffProfile);
      setBranchName(staffProfile.branch_name || staffProfile.branch?.name || "");
      const availableBranches = results[3].status === "fulfilled" && Array.isArray(results[3].value)
        ? results[3].value
        : [];
      setAttendanceBranches(availableBranches);
      setAttendanceBranchId((current) => availableBranches.some((branch) => String(branch.branch_id) === String(current))
        ? current
        : String(staffProfile.branch_id || availableBranches[0]?.branch_id || ""));
      if (staffProfile.branch_id) {
        const [branchResult] = await Promise.allSettled([
          api(`/branches/${staffProfile.branch_id}`, token),
        ]);
        if (branchResult.status === "fulfilled") {
          setBranchName(staffProfile.branch_name || staffProfile.branch?.name || branchResult.value.name || "");
        }
      }
    } else setError("We couldn't load your profile. Please refresh the page.");
    if (includeAttendance) {
      const attendanceEmployeeId = results[0].status === "fulfilled"
        ? results[0].value.employee_id || employeeId
        : employeeId;
      const historyResult = await Promise.allSettled([
        api(`/attendance/employee/${encodeURIComponent(attendanceEmployeeId)}/history?page_size=100`, token),
      ]);
      if (historyResult[0].status === "fulfilled") {
        const history = historyResult[0].value;
        setRecords(Array.isArray(history) ? history : Array.isArray(history?.items) ? history.items : []);
      } else {
        setRecords([]);
        setError("We couldn't load your attendance history. Please try again.");
      }
      setAttendanceLoading(false);
    }
    if (includeRequests && results[2].status === "fulfilled") {
      setRequests(Array.isArray(results[2].value) ? results[2].value : []);
    }
  }, [employeeId, token]);
  useEffect(() => {
    refresh({
      includeAttendance: section === "Home" || section === "Attendance",
      includeRequests: section === "Home" || section === "Requests",
      includeHomeData: section === "Home",
    });
  }, [refresh, section]);
  useEffect(() => {
    if (!attendanceBranchId || !token) {
      setShifts([]);
      return undefined;
    }
    let ignore = false;
    setShifts([]);
    setShiftId("");
    api(`/attendance/available-shifts?branch_id=${encodeURIComponent(attendanceBranchId)}`, token)
      .then((items) => { if (!ignore) setShifts(Array.isArray(items) ? items : []); })
      .catch(() => { if (!ignore) setShifts([]); });
    return () => { ignore = true; };
  }, [attendanceBranchId, token]);
  const playAnnouncementChime = useCallback(() => {
    if (!announcementSoundEnabled) return;
    const AudioContextClass = window.AudioContext || window.webkitAudioContext;
    if (!AudioContextClass) return;
    try {
      const context = announcementAudioRef.current || new AudioContextClass();
      announcementAudioRef.current = context;
      if (context.state === "suspended") context.resume().catch(() => {});
      const startAt = context.currentTime + 0.03;
      [659.25, 783.99, 987.77].forEach((frequency, index) => {
        const start = startAt + index * 0.17;
        const oscillator = context.createOscillator();
        const gain = context.createGain();
        oscillator.type = "sine";
        oscillator.frequency.value = frequency;
        gain.gain.setValueAtTime(0.0001, start);
        gain.gain.exponentialRampToValueAtTime(0.12, start + 0.025);
        gain.gain.exponentialRampToValueAtTime(0.0001, start + 0.48);
        oscillator.connect(gain);
        gain.connect(context.destination);
        oscillator.start(start);
        oscillator.stop(start + 0.5);
      });
    } catch { /* Audio may be unavailable or blocked until the user enables it. */ }
  }, [announcementSoundEnabled]);
  const loadAnnouncements = useCallback(async () => {
    try {
      const data = await api(`/announcements/me?page=${announcementPage + 1}&page_size=3`, token);
      const nextAnnouncements = Array.isArray(data) ? data : Array.isArray(data.items) ? data.items : [];
      const nextIds = new Set(nextAnnouncements.map((item) => String(item.id)));
      if (announcementIdsRef.current) {
        const hasNewUnread = nextAnnouncements.some((item) => !item.is_read && !announcementIdsRef.current.has(String(item.id)));
        if (hasNewUnread) playAnnouncementChime();
      }
      announcementIdsRef.current = nextIds;
      setAnnouncements(nextAnnouncements);
      setAnnouncementTotal(Array.isArray(data) ? data.length : Number(data.total) || 0);
      setAnnouncementTotalPages(Array.isArray(data) ? Math.max(1, Math.ceil(data.length / 3)) : Math.max(1, Number(data.total_pages) || 1));
    }
    catch (loadError) { setError(loadError.message || "Could not load announcements."); }
  }, [token, playAnnouncementChime, announcementPage]);
  useEffect(() => {
    if (section !== "Announcements") return undefined;
    loadAnnouncements();
    const timer = window.setInterval(loadAnnouncements, 30_000);
    return () => window.clearInterval(timer);
  }, [loadAnnouncements, section]);
  useEffect(() => {
    if (!announcementSoundEnabled) return undefined;
    const unlockAudio = () => {
      const AudioContextClass = window.AudioContext || window.webkitAudioContext;
      if (!AudioContextClass) return;
      try {
        const context = announcementAudioRef.current || new AudioContextClass();
        announcementAudioRef.current = context;
        if (context.state === "suspended") context.resume().catch(() => {});
      } catch { /* Keep the announcement inbox usable if browser audio is unavailable. */ }
    };
    document.addEventListener("pointerdown", unlockAudio, { once: true });
    document.addEventListener("keydown", unlockAudio, { once: true });
    return () => {
      document.removeEventListener("pointerdown", unlockAudio);
      document.removeEventListener("keydown", unlockAudio);
    };
  }, [announcementSoundEnabled]);
  useEffect(() => () => { announcementAudioRef.current?.close().catch(() => {}); }, []);
  const toggleAnnouncementSound = async () => {
    const enabled = !announcementSoundEnabled;
    setAnnouncementSoundEnabled(enabled);
    localStorage.setItem(`hiwaga_announcement_sound_${employeeId}`, String(enabled));
    if (enabled) {
      const AudioContextClass = window.AudioContext || window.webkitAudioContext;
      if (!AudioContextClass) { setError("This browser does not support announcement sounds."); return; }
      try {
        const context = announcementAudioRef.current || new AudioContextClass();
        announcementAudioRef.current = context;
        await context.resume();
        const oscillator = context.createOscillator();
        const gain = context.createGain();
        oscillator.type = "sine";
        oscillator.frequency.value = 783.99;
        gain.gain.setValueAtTime(0.0001, context.currentTime);
        gain.gain.exponentialRampToValueAtTime(0.1, context.currentTime + 0.025);
        gain.gain.exponentialRampToValueAtTime(0.0001, context.currentTime + 0.3);
        oscillator.connect(gain); gain.connect(context.destination);
        oscillator.start(); oscillator.stop(context.currentTime + 0.32);
      } catch { setError("Could not enable announcement sound in this browser."); }
    }
  };
  const closePasswordModal = () => {
    setPasswordModalOpen(false);
    setPasswordCurrent("");
    setPasswordNew("");
    setPasswordConfirm("");
    setPasswordMessage("");
    setPasswordError("");
  };
  const submitPasswordChange = async (event) => {
    event.preventDefault();
    setPasswordMessage("");
    setPasswordError("");
    if (passwordNew.length < 8) {
      setPasswordError("New password must be at least 8 characters.");
      return;
    }
    if (passwordNew !== passwordConfirm) {
      setPasswordError("The new passwords do not match.");
      return;
    }
    setPasswordBusy(true);
    try {
      const result = await api("/auth/change-password", token, {
        method: "POST",
        body: JSON.stringify({ old_password: passwordCurrent, new_password: passwordNew }),
      });
      setPasswordMessage(result.message || "Password changed successfully.");
      setPasswordCurrent("");
      setPasswordNew("");
      setPasswordConfirm("");
    } catch (changeError) {
      setPasswordError(changeError.message || "Unable to change password.");
    } finally {
      setPasswordBusy(false);
    }
  };
  const markAnnouncementRead = async (item) => {
    if (item.is_read) return;
    try {
      await api(`/announcements/${encodeURIComponent(item.id)}/read`, token, { method: "POST" });
      setAnnouncements((current) => current.map((entry) => entry.id === item.id ? { ...entry, is_read: true } : entry));
    } catch (readError) { setError(readError.message || "Could not mark announcement as read."); }
  };
  const markAllAnnouncementsRead = async () => {
    const unread = announcements.filter((item) => !item.is_read);
    if (!unread.length || announcementReadBusy) return;
    setAnnouncementReadBusy(true);
    setError("");
    const results = await Promise.allSettled(unread.map((item) =>
      api(`/announcements/${encodeURIComponent(item.id)}/read`, token, { method: "POST" }),
    ));
    const readIds = new Set(unread.filter((_, index) => results[index].status === "fulfilled").map((item) => item.id));
    setAnnouncements((current) => current.map((item) => readIds.has(item.id) ? { ...item, is_read: true } : item));
    const failureCount = results.filter((result) => result.status === "rejected").length;
    if (failureCount) setError(`Could not mark ${failureCount} announcement${failureCount === 1 ? "" : "s"} as read.`);
    else setNotice("All announcements marked as read.");
    setAnnouncementReadBusy(false);
  };
  useEffect(() => () => streamRef.current?.getTracks().forEach((track) => track.stop()), []);
  useEffect(() => {
    if (!profileMenuOpen) return undefined;
    const closeOnOutsideClick = (event) => {
      if (!profileMenuRef.current?.contains(event.target)) setProfileMenuOpen(false);
    };
    const closeOnEscape = (event) => {
      if (event.key === "Escape") setProfileMenuOpen(false);
    };
    document.addEventListener("pointerdown", closeOnOutsideClick);
    document.addEventListener("keydown", closeOnEscape);
    return () => {
      document.removeEventListener("pointerdown", closeOnOutsideClick);
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, [profileMenuOpen]);
  useEffect(() => {
    if (!profilePhotoMenuOpen && !profilePhotoPreviewOpen) return undefined;
    const closeOnOutsideClick = (event) => {
      if (profilePhotoMenuOpen && !profilePhotoAreaRef.current?.contains(event.target)) setProfilePhotoMenuOpen(false);
    };
    const closeOnEscape = (event) => {
      if (event.key === "Escape") { setProfilePhotoMenuOpen(false); setProfilePhotoPreviewOpen(false); }
    };
    document.addEventListener("pointerdown", closeOnOutsideClick);
    document.addEventListener("keydown", closeOnEscape);
    return () => { document.removeEventListener("pointerdown", closeOnOutsideClick); document.removeEventListener("keydown", closeOnEscape); };
  }, [profilePhotoMenuOpen, profilePhotoPreviewOpen]);
  useEffect(() => {
    if (!cameraOpen || !videoRef.current || !streamRef.current) return;
    videoRef.current.srcObject = streamRef.current;
    videoRef.current.play().catch(() => {});
  }, [cameraOpen]);

  const todayRecords = useMemo(() => records.filter((record) => asDate(record.timestamp)?.toDateString() === new Date().toDateString()), [records]);
  const attendanceMonthRecords = useMemo(() => {
    // First, create entries from existing records
    const days = records.reduce((grouped, record) => {
      const date = asDate(record.timestamp);
      if (!date || `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}` !== attendanceMonth) return grouped;
      const dayKey = `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;
      const day = grouped[dayKey] || (grouped[dayKey] = {
        date: new Date(date.getFullYear(), date.getMonth(), date.getDate()),
        checkIn: null,
        checkOut: null,
      });
      if (record.check_type === "in" && (!day.checkIn || date < day.checkIn)) day.checkIn = date;
      if (record.check_type === "out" && (!day.checkOut || date > day.checkOut)) day.checkOut = date;
      return grouped;
    }, {});

    // Now fill in missing days for the selected month from DOJ to today
    const [year, month] = attendanceMonth.split('-').map(Number);
    const monthStart = new Date(year, month - 1, 1);
    const monthEnd = new Date(year, month, 0);
    const today = new Date();
    const dojDate = profile?.date_of_joining ? new Date(profile.date_of_joining) : monthStart;
    
    // Start from the later of: month start or DOJ
    const startDate = dojDate > monthStart ? dojDate : monthStart;
    // End at the earlier of: month end or today
    const endDate = today < monthEnd ? today : monthEnd;
    
    // Fill in all days from start to end
    const currentDate = new Date(startDate);
    while (currentDate <= endDate) {
      const dayKey = `${currentDate.getFullYear()}-${currentDate.getMonth()}-${currentDate.getDate()}`;
      if (!days[dayKey]) {
        days[dayKey] = {
          date: new Date(currentDate),
          checkIn: null,
          checkOut: null,
        };
      }
      currentDate.setDate(currentDate.getDate() + 1);
    }

    return Object.values(days).sort((first, second) => second.date - first.date);
  }, [attendanceMonth, records, profile?.date_of_joining]);
  const attendancePageSize = 10;
  const attendancePageCount = Math.max(1, Math.ceil(attendanceMonthRecords.length / attendancePageSize));
  const visibleAttendancePage = Math.min(attendancePage, attendancePageCount - 1);
  const visibleAttendanceRecords = attendanceMonthRecords.slice(visibleAttendancePage * attendancePageSize, (visibleAttendancePage + 1) * attendancePageSize);
  const formatAttendanceTime = (date) => date?.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) || "-";
  const formatWorkingHours = (day) => {
    if (!day.checkIn || !day.checkOut) return "-";
    const minutes = Math.max(0, Math.round((day.checkOut - day.checkIn) / 60000));
    return `${Math.floor(minutes / 60)}h ${minutes % 60}m`;
  };
  const checkedIn = todayRecords.some((record) => record.check_type === "in");
  const checkedOut = todayRecords.some((record) => record.check_type === "out");
  const pendingRequests = requests.filter((request) => request.status === "pending").length;
  const displayName = profile?.full_name || "Team member";
  const initials = displayName.split(/\s+/).slice(0, 2).map((part) => part[0]).join("").toUpperCase();
  const navItems = [[House, "Home"], [Clock3, "Attendance"], [CalendarDays, "Requests"], [Megaphone, "Announcements"]];
  const profileGroups = [
    {
      id: "personal",
      label: "Personal",
      fields: [
        ["Full name", profile?.full_name],
        ["Father / husband name", profile?.father_husband_name],
        ["Email", profile?.email],
        ["Phone", profile?.phone],
        ["Gender", profile?.gender],
        ["Date of birth", profile?.date_of_birth],
        ["Category", profile?.category],
        ["Marital status", profile?.marital_status],
        ["Aadhaar number", profile?.aadhaar_number],
        ["Address", profile?.address],
      ],
    },
    {
      id: "employment",
      label: "Employment",
      fields: [
        ["Employee Code", profile?.employee_code],
        ["Designation", profile?.designation],
        ["Department", profile?.department],
        ["Date of joining", profile?.date_of_joining],
        ["Last working day (LWD)", profile?.last_working_day],
        ["Role", profile?.role_code || profile?.role],
        ["Branch", branchName || profile?.branch_name || profile?.branch_id],
        ["Employment status", profile?.employment_status],
        ["Previous employment joining date", profile?.previous_employment_joining_date],
      ],
    },
    {
      id: "payroll",
      label: "Payroll & statutory",
      fields: [
        ["PAN number", profile?.pan_number],
        ["UAN number", profile?.uan_number],
        ["EPF number", profile?.epf_number],
        ["ESIC number", profile?.esic_number],
        ["Bank name", profile?.bank_name],
        ["Bank account number", profile?.bank_account_number],
        ["IFSC", profile?.bank_ifsc],
        ["Bank branch", profile?.bank_branch],
        ["Pay scale", profile?.pay_scale],
        ["Basic salary", profile?.basic_salary],
        ["HRA", profile?.hra],
        ["Other allowances", profile?.other_allowances],
      ],
    },
  ];
  const activeProfileGroup = profileGroups.find((group) => group.id === profileTab) || profileGroups[0];

  const handleProfilePhotoChange = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      setError("Choose an image file for your profile photo.");
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      setError("Choose a profile photo smaller than 2 MB.");
      return;
    }
    setProfilePhotoBusy(true);
    setError("");
    setNotice("");
    try {
      const formData = new FormData();
      formData.append("photo", file, file.name);
      await api("/auth/me/photo", token, { method: "POST", body: formData });
      const updatedProfile = await api("/auth/me", token);
      setProfile(updatedProfile);
      setNotice("Profile photo updated.");
    } catch (uploadError) {
      setError(uploadError.message || "Unable to upload profile photo.");
    } finally {
      setProfilePhotoBusy(false);
    }
  };

  const openCamera = async () => {
    if (!navigator.mediaDevices?.getUserMedia) {
      setError("Camera access is unavailable. Open this page on localhost or HTTPS and allow camera access.");
      return;
    }
    try {
      streamRef.current = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "user" }, audio: false });
      setError(""); setSelfie(null); setSelfiePreview(""); setCameraOpen(true);
    } catch {
      setError("Camera permission was denied or the camera is unavailable.");
    }
  };
  const closeCamera = () => {
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
    if (videoRef.current) videoRef.current.srcObject = null;
    setCameraOpen(false);
  };
  const captureSelfie = () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas) return;
    canvas.width = video.videoWidth || 640; canvas.height = video.videoHeight || 480;
    const context = canvas.getContext("2d");
    if (!context) return;
    context.drawImage(video, 0, 0, canvas.width, canvas.height);
    canvas.toBlob((blob) => {
      if (!blob) return;
      setSelfie(new File([blob], `selfie-${Date.now()}.jpg`, { type: "image/jpeg" }));
      setSelfiePreview(URL.createObjectURL(blob));
      closeCamera();
    }, "image/jpeg", 0.92);
  };
  const beginAttendance = (type) => {
    setAttendanceType(type); setError(""); setNotice(""); setSelfie(null); setSelfiePreview("");
    setShiftId("");
    if (attendanceBranches.length > 1) setAttendanceBranchId("");
    if (type === "in" || attendanceBranches.length > 1) setShiftDialogOpen(true);
    else openCamera();
  };
  const continueToCamera = () => {
    if (!attendanceBranchId) { setError("Select the branch where you are recording attendance."); return; }
    if (attendanceType === "in" && !shiftId) { setError("Select your work shift to continue."); return; }
    setShiftDialogOpen(false); openCamera();
  };
  const submitAttendance = async () => {
    const type = attendanceType;
    setBusy(true); setError(""); setNotice("");
    try {
      if (!selfie) throw new Error("Capture a verification selfie before recording attendance.");
      if (!attendanceBranchId) throw new Error("Select the branch where you are recording attendance.");
      if (type === "in" && !shiftId) throw new Error("Select your shift before checking in.");
      if (!navigator.geolocation) throw new Error("Location services are unavailable in this browser.");
      const position = await new Promise((resolve, reject) => navigator.geolocation.getCurrentPosition(resolve, () => reject(new Error("Allow location access to record attendance.")), { enableHighAccuracy: true, timeout: 15000 }));
      const body = new FormData();
      body.append("latitude", position.coords.latitude.toFixed(6)); body.append("longitude", position.coords.longitude.toFixed(6));
      body.append("branch_id", attendanceBranchId);
      body.append("device_info", navigator.userAgent); body.append("selfie", selfie);
      if (type === "in") body.append("shift_id", shiftId);
      await api(`/attendance/${type === "in" ? "checkin" : "checkout"}`, token, { method: "POST", body });
      setNotice(type === "in" ? "You’re checked in. Have a great shift!" : "You’re checked out. Your day is complete.");
      setSelfie(null); if (selfiePreview) URL.revokeObjectURL(selfiePreview); setSelfiePreview("");
      await refresh({ includeAttendance: true, includeRequests: section === "Home", includeHomeData: true });
    } catch (submitError) {
      setShiftDialogOpen(false);
      closeCamera();
      if (selfiePreview) URL.revokeObjectURL(selfiePreview);
      setSelfiePreview("");
      setSelfie(null);
      setError(submitError.message || `Unable to check ${type === "in" ? "in" : "out"}.`);
    }
    finally { setBusy(false); }
  };
  const submitRequest = async (event) => {
    event.preventDefault(); setBusy(true); setError(""); setNotice("");
    const payload = requestType === "leave"
      ? { from_date: fromDate, to_date: toDate, reason }
      : requestType === "early_logout"
        ? { permission_date: permissionDate, expected_departure_time: departureTime || null, reason }
        : { permission_date: permissionDate, expected_arrival_time: arrivalTime || null, reason };
    const originalRole = localStorage.getItem("hiwaga_role");
    const shouldUseStaffRole = String(originalRole || "").trim().toLowerCase().replace(/[\s-]+/g, "_") === "super_admin";
    if (shouldUseStaffRole) localStorage.setItem("hiwaga_role", "staff");
    try {
      const requestRoute = requestType === "leave" ? "leave" : requestType === "early_logout" ? "early-logout" : "late-permission";
      await api(`/requests/${requestRoute}`, token, { method: "POST", body: JSON.stringify(payload) });
      setNotice("Your request was submitted for review."); setReason(""); setFromDate(""); setToDate(""); setPermissionDate(""); setArrivalTime(""); setDepartureTime(""); await refresh({ includeAttendance: section === "Home", includeRequests: true, includeHomeData: section === "Home" });
    } catch (submitError) { setError(submitError.message); }
    finally {
      if (shouldUseStaffRole) {
        if (originalRole) localStorage.setItem("hiwaga_role", originalRole);
        else localStorage.removeItem("hiwaga_role");
      }
      setBusy(false);
    }
  };
  const go = (item) => {
    sectionRef.current = item;
    setSection(item);
    onNavigate?.(item);
    setMobileOpen(false);
    setError("");
    setNotice("");
  };
  useEffect(() => {
    const handlePopState = () => {
      const nextSection = getStaffSectionForPath(window.location.pathname) || "Home";
      if (!getStaffSectionForPath(window.location.pathname)) {
        window.history.replaceState({}, "", "/");
      }
      if (nextSection === sectionRef.current) return;
      sectionFromHistoryRef.current = true;
      sectionRef.current = nextSection;
      setSection(nextSection);
    };
    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, []);
  useEffect(() => {
    if (sectionFromHistoryRef.current) {
      sectionFromHistoryRef.current = false;
      return;
    }
    const targetPath = STAFF_PATH_BY_SECTION[section] || "/";
    if (!getStaffSectionForPath(window.location.pathname)) {
      window.history.replaceState({}, "", targetPath);
      return;
    }
    if (window.location.pathname !== targetPath) {
      window.history.pushState({}, "", targetPath);
    }
  }, [section]);
  const announcementPageCount = announcementTotalPages;
  const visibleAnnouncementPage = Math.min(announcementPage, announcementPageCount - 1);
  const visibleAnnouncements = announcements;
  const requestPageSize = 5;
  const requestPageCount = Math.max(1, Math.ceil(requests.length / requestPageSize));
  const visibleRequestPage = Math.min(requestPage, requestPageCount - 1);
  const visibleRequests = requests.slice(visibleRequestPage * requestPageSize, (visibleRequestPage + 1) * requestPageSize);

  return <div className="staff-app">
    <aside className={`staff-sidebar ${mobileOpen ? "is-open" : ""}`} style={mobileOpen ? {zIndex: 1100, position: 'fixed'} : {}}>
      <div className="staff-brand"><img src="/hiwaga-logo.png" alt="Hiwaga" /> <button className="staff-close" onClick={() => setMobileOpen(false)} aria-label="Close menu"><X size={18}/></button></div>
      <nav>{navItems.map(([Icon, label]) => <button key={label} className={section === label ? "active" : ""} onClick={() => go(label)}><Icon size={18}/>{label}{label === "Requests" && pendingRequests > 0 && <span className="staff-nav-count">{pendingRequests}</span>}</button>)}</nav>
      <div className="staff-sidebar-bottom"><button className={section === "My profile" ? "active" : ""} onClick={() => go("My profile")}><UserRound size={17}/>My profile</button></div>
    </aside>
    {mobileOpen && <button className="staff-scrim" onClick={() => setMobileOpen(false)} style={{zIndex: 1099, position: 'fixed', inset: 0}} aria-label="Close navigation"/>}
    <main className="staff-main">
      <header className="staff-topbar"><button className="staff-menu" onClick={() => setMobileOpen(true)} aria-label="Open navigation"><Menu/></button><div className="staff-breadcrumb"><b>{section}</b></div><div className="staff-top-actions"><span className="staff-date-top">{new Date().toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" })}</span><div className="staff-profile-menu" ref={profileMenuRef}><button className="staff-profile-trigger" aria-haspopup="menu" aria-expanded={profileMenuOpen} onClick={() => setProfileMenuOpen((open) => !open)}><span className="staff-avatar">{initials || "TM"}</span><span className="staff-account"><b>{displayName}</b><small>{profile?.designation || "Team member"}</small></span></button>{profileMenuOpen && <div className="staff-profile-dropdown" role="menu"><button role="menuitem" onClick={() => { setProfileMenuOpen(false); go("My profile"); }}><UserRound size={16}/>Profile</button><button role="menuitem" onClick={() => { setProfileMenuOpen(false); setPasswordMessage(""); setPasswordError(""); setPasswordModalOpen(true); }}><ShieldCheck size={16}/>Change password</button><button role="menuitem" onClick={onLogout}><LogOut size={16}/>Sign out</button></div>}</div></div></header>
      <div className="staff-content">
        <div className={`staff-page-heading ${section === "Announcements" ? "staff-announcements-page-heading" : ""} ${section === "Attendance" ? "staff-attendance-page-heading" : ""}`} style={section === "Attendance" ? {justifyContent: 'flex-start', textAlign: 'left'} : {}}><div style={section === "Attendance" ? {textAlign: 'left', width: '100%'} : {}}><h1 style={section === "Attendance" ? {textAlign: 'left'} : {}}>{section === "Home" ? `Good ${new Date().getHours() < 12 ? "morning" : new Date().getHours() < 17 ? "afternoon" : "evening"}, ${displayName.split(" ")[0]}` : section}</h1><p>{section === "Home" ? "Here’s your day at a glance. Everything you need, in one place." : section === "Attendance" ? "Record today’s attendance and review your recent activity." : section === "Requests" ? "Request time away or a schedule adjustment, then track its status." : section === "Announcements" ? "Stay up to date with important messages and updates from your organization." : "Your work details and team information."}</p></div><div className="staff-heading-mark">{section === "Announcements" ? <Megaphone size={18}/> : <Activity size={18}/>}<span>{section === "Announcements" ? "Team updates" : "Personal workspace"}</span></div></div>
        {(error || notice) && <div className={`staff-feedback ${error ? "error" : "success"}`} role="status"><span>{error || notice}</span><button onClick={() => { setError(""); setNotice(""); }} aria-label="Dismiss"><X size={16}/></button></div>}
        {section === "Attendance" && <section className="staff-card staff-attendance-history">
          <div className="staff-card-head">
            <div><h3>Attendance history</h3></div>
            <label className="staff-attendance-month">Month<input type="month" value={attendanceMonth} onChange={(event) => { setAttendanceMonth(event.target.value); setAttendancePage(0); }}/></label>
          </div>
          {attendanceLoading ? <div className="staff-attendance-empty">Loading your attendance records…</div> : attendanceMonthRecords.length ? <div className="staff-attendance-table-wrap">
            <table className="staff-attendance-table">
              <thead><tr><th>Date</th><th>Check-in</th><th>Check-out</th><th>Working hours</th><th>Status</th></tr></thead>
              <tbody>{visibleAttendanceRecords.map((day) => <tr key={day.date.toISOString()}>
                <td>{day.date.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric", year: "numeric" })}</td>
                <td>{formatAttendanceTime(day.checkIn)}</td>
                <td>{formatAttendanceTime(day.checkOut)}</td>
                <td>{formatWorkingHours(day)}</td>
                <td><span className={`staff-record-status ${day.checkIn ? "verified" : "review"}`}>{day.checkIn ? "Present" : "Absent"}</span></td>
              </tr>)}</tbody>
            </table>
          </div> : <div className="staff-attendance-empty"><Clock3/><b>No attendance records for this month</b><span>Choose another month to review your attendance history.</span></div>}
          {!attendanceLoading && attendanceMonthRecords.length > attendancePageSize && <footer className="staff-attendance-pagination"><span>{visibleAttendancePage * attendancePageSize + 1}–{Math.min((visibleAttendancePage + 1) * attendancePageSize, attendanceMonthRecords.length)} of {attendanceMonthRecords.length}</span><div><button type="button" disabled={visibleAttendancePage === 0} aria-label="Previous attendance page" onClick={() => setAttendancePage((current) => Math.max(0, current - 1))}>‹</button><span>{visibleAttendancePage + 1}</span><button type="button" disabled={visibleAttendancePage + 1 >= attendancePageCount} aria-label="Next attendance page" onClick={() => setAttendancePage((current) => Math.min(attendancePageCount - 1, current + 1))}>›</button></div></footer>}
        </section>}
        {section === "Announcements" && <section className="staff-card staff-announcements">
          <div className="staff-card-head staff-announcement-card-head"><div><h3><span>Hiwaga</span> Announcements</h3><p>Stay updated with important notifications and updates</p></div><div className="staff-announcement-actions"><button type="button" className={`staff-announcement-sound-toggle ${announcementSoundEnabled ? "sound-enabled" : ""}`} onClick={toggleAnnouncementSound} aria-label={announcementSoundEnabled ? "Turn announcement sound off" : "Turn announcement sound on"} title={announcementSoundEnabled ? "Turn announcement sound off" : "Turn announcement sound on"}>{announcementSoundEnabled ? <Volume2 size={18}/> : <VolumeX size={18}/>}</button><button type="button" disabled={announcementReadBusy || !announcements.some((item) => !item.is_read)} onClick={markAllAnnouncementsRead}><CheckCircle2 size={15}/>{announcementReadBusy ? "Marking…" : "Mark all as read"}</button><button type="button" onClick={loadAnnouncements}>Refresh</button></div></div>
          {announcements.length ? visibleAnnouncements.map((item) => {
            const mediaItems = announcementMediaItems(item);
            const senderName = item.name || "Team member";
            const senderDesignation = item.designation ? item.designation.replace(/_/g, "-") : "";
            return <article className={`staff-announcement ${item.is_read ? "is-read" : "is-unread"}`} key={item.id}>
              <div className="staff-announcement-top"><h4>{item.title}</h4><div className="staff-announcement-byline"><span>{senderName}{senderDesignation ? ` (${senderDesignation})` : ""}</span><time>{asDate(item.created_at)?.toLocaleString() || item.created_at}</time></div></div>
              {item.body && <p>{item.body}</p>}{mediaItems.length > 0 && <div className={`staff-announcement-media-gallery${mediaItems.length === 1 ? " is-single" : ""}`} aria-label={`${mediaItems.length} attachments`}>{mediaItems.slice(0, 4).map((mediaItem, index) => <button className="staff-announcement-media-tile" type="button" key={mediaItem.id || mediaItem.media_url || index} onClick={() => setAnnouncementViewer({ title: item.title, items: mediaItems, index })} aria-label={`View attachment ${index + 1}${mediaItems.length > 4 && index === 3 ? ` and ${mediaItems.length - 4} more` : ""}`}>{mediaItem.media_content_type?.startsWith("video/") ? <video muted playsInline preload="metadata" src={mediaItem.media_url}/> : <img src={mediaItem.media_url} alt={mediaItem.media_filename || item.title}/ >}{mediaItems.length > 4 && index === 3 && <span className="staff-announcement-media-more">+{mediaItems.length - 4}</span>}</button>)}</div>}<button className="staff-announcement-read" type="button" disabled={item.is_read} onClick={() => markAnnouncementRead(item)}>{item.is_read ? "Read" : "Mark as read"}</button>
            </article>;
          }) : <div className="staff-empty"><Bell/><b>No announcements yet</b><span>New updates from your organization will appear here.</span></div>}
          <footer className="staff-announcement-pagination"><span>{announcementTotal ? `${visibleAnnouncementPage * 3 + 1}–${Math.min((visibleAnnouncementPage + 1) * 3, announcementTotal)} of ${announcementTotal}` : "0 announcements"}</span><div><button type="button" disabled={visibleAnnouncementPage === 0} aria-label="Previous announcement page" onClick={() => setAnnouncementPage((current) => Math.max(0, current - 1))}>‹</button><span>{visibleAnnouncementPage + 1}</span><button type="button" disabled={visibleAnnouncementPage + 1 >= announcementPageCount} aria-label="Next announcement page" onClick={() => setAnnouncementPage((current) => Math.min(announcementPageCount - 1, current + 1))}>›</button></div></footer>
        </section>}
        {announcementViewer && <div className="staff-announcement-media-viewer" role="dialog" aria-modal="true" aria-label={`${announcementViewer.title} attachments`} onClick={(event) => { if (event.target === event.currentTarget) setAnnouncementViewer(null); }}><section className="staff-announcement-media-viewer-panel"><header><span>{announcementViewer.title} · {announcementViewer.index + 1} of {announcementViewer.items.length}</span><button type="button" onClick={() => setAnnouncementViewer(null)} aria-label="Close attachment viewer"><X size={20}/></button></header><div className="staff-announcement-media-viewer-stage">{announcementViewer.index > 0 && <button type="button" className="staff-announcement-media-viewer-nav previous" onClick={() => setAnnouncementViewer((current) => ({ ...current, index: current.index - 1 }))} aria-label="Previous attachment"><ChevronLeft size={22}/></button>}{announcementViewer.items[announcementViewer.index].media_content_type?.startsWith("video/") ? <video controls autoPlay src={announcementViewer.items[announcementViewer.index].media_url}/> : <img src={announcementViewer.items[announcementViewer.index].media_url} alt={announcementViewer.items[announcementViewer.index].media_filename || announcementViewer.title}/ >}{announcementViewer.index < announcementViewer.items.length - 1 && <button type="button" className="staff-announcement-media-viewer-nav next" onClick={() => setAnnouncementViewer((current) => ({ ...current, index: current.index + 1 }))} aria-label="Next attachment"><ChevronRight size={22}/></button>}</div></section></div>}
        {section === "Home" && <>
          <section className="staff-hero"><div className="staff-hero-copy"><span className="staff-live"><i/> TODAY · {new Date().toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" })}</span><h2>{checkedOut ? "You’ve completed your day" : checkedIn ? "You’re checked in" : "Ready to start your day?"}</h2><p>{checkedOut ? "Your attendance has been recorded. See you next time." : checkedIn ? "Your workday is underway. Remember to check out when you finish." : "Check in to log your attendance, shift and work location."}</p><div className="staff-hero-actions">{!checkedIn && <button className="staff-button light" onClick={() => beginAttendance("in")} disabled={busy || attendanceBranches.length === 0}>{busy ? "Recording…" : <><CheckCircle2 size={17}/> Check in</>}</button>}{checkedIn && !checkedOut && <button className="staff-button light" onClick={() => beginAttendance("out")} disabled={busy}>{busy ? "Recording…" : <><LogOut size={17}/> Check out</>}</button>}<button className="staff-button ghost" onClick={() => go("Attendance")}>View attendance <ChevronRight size={16}/></button></div><div className="staff-hero-note"><MapPin size={14}/> {attendanceType === "out" && selfie ? "Selfie captured. Submit to verify your location." : "A selfie and location verification are required to record attendance."}</div></div><div className="staff-hero-art"><div className="staff-orbit orbit-one"/><div className="staff-orbit orbit-two"/><div className="staff-clock-icon"><Clock3 size={40}/></div></div></section>
          <section className="staff-metric-grid"><article><div className="staff-metric-icon green"><Clock3/></div><span>CHECK-IN</span><b>{timeLabel(todayRecords.find((record) => record.check_type === "in")?.timestamp)}</b><small>{checkedIn ? "Recorded today" : "Not recorded yet"}</small></article><article><div className="staff-metric-icon blue"><LogOut/></div><span>CHECK-OUT</span><b>{timeLabel(todayRecords.find((record) => record.check_type === "out")?.timestamp)}</b><small>{checkedOut ? "Day complete" : "When you finish"}</small></article><article><div className="staff-metric-icon violet"><CalendarDays/></div><span>MY REQUESTS</span><b>{pendingRequests} pending</b><small>{requests.length} total requests</small></article><article><div className="staff-metric-icon amber"><BriefcaseBusiness/></div><span>MY TEAM</span><b>{branchName || (profile?.branch_id ? `Branch ${profile.branch_id}` : "Unassigned")}</b><small>{profile?.designation || profile?.department || "Work location"}</small></article></section>
          <section className="staff-lower-grid"><article className="staff-card"><div className="staff-card-head"><div><span className="staff-eyebrow">ACTIVITY</span><h3>Today’s timeline</h3></div><button onClick={() => go("Attendance")}>Full history <ChevronRight size={15}/></button></div>{todayRecords.length ? <div className="staff-timeline">{todayRecords.map((record) => <div className="staff-timeline-row" key={record.id || record.timestamp}><span className={`staff-timeline-dot ${record.check_type === "in" ? "green" : "blue"}`}/><div><b>{record.check_type === "in" ? "Checked in" : "Checked out"}</b><small>{record.geofence_status === "pass" ? "Within approved location" : "Location recorded"}</small></div><time>{timeLabel(record.timestamp)}</time></div>)}</div> : <div className="staff-empty"><Clock3/><b>Your day starts here</b><span>Your check-in and check-out will appear in this timeline.</span></div>}</article><article className="staff-card"><div className="staff-card-head"><div><span className="staff-eyebrow">UPDATES</span><h3>My requests</h3></div><button onClick={() => go("Requests")}>Open requests <ChevronRight size={15}/></button></div>{requests.length ? requests.slice(0, 3).map((request) => <div className="staff-request-row" key={request.id || request.created_at}><span className="staff-request-icon"><FileText size={16}/></span><div><b>{requestLabel(request)}</b><small>{requestSchedule(request)}</small></div><span className={`staff-status ${String(request.status).toLowerCase()}`}>{request.status}</span></div>) : <div className="staff-empty compact"><FileText/><b>No requests yet</b><span>Request leave or a schedule adjustment.</span><button onClick={() => go("Requests")}>Create a request <ChevronRight size={14}/></button></div>}</article></section>
        </>}
        {section === "Requests" && (
          <section className="staff-request-layout">
            <article className="staff-card staff-form-card">
              <div className="staff-card-head">
                <div><span className="staff-eyebrow">REQUEST CENTER</span><h3>Submit a request</h3></div>
              </div>
              <form className="staff-request-form" onSubmit={submitRequest}>
                <label>Request type<select value={requestType} onChange={(event) => setRequestType(event.target.value)}><option value="leave">Leave request</option><option value="late_permission">Late login</option><option value="early_logout">Early logout</option></select></label>
                {requestType === "leave" ? <div className="staff-form-pair"><label>From<input type="date" value={fromDate} onChange={(event) => setFromDate(event.target.value)} required/></label><label>To<input type="date" value={toDate} onChange={(event) => setToDate(event.target.value)} min={fromDate} required/></label></div> : <div className="staff-form-pair"><label>Date<input type="date" value={permissionDate} onChange={(event) => setPermissionDate(event.target.value)} required/></label><label>{requestType === "early_logout" ? "Expected departure" : "Expected arrival"}<input type="time" value={requestType === "early_logout" ? departureTime : arrivalTime} onChange={(event) => requestType === "early_logout" ? setDepartureTime(event.target.value) : setArrivalTime(event.target.value)}/></label></div>}
                <label>Reason<textarea value={reason} onChange={(event) => setReason(event.target.value)} placeholder="Add a short note for your manager" rows="4" required/></label>
                <button className="staff-button primary" disabled={busy}>{busy ? "Submitting…" : <><ArrowDownToLine size={16}/> Submit for review</>}</button>
              </form>
            </article>
            <article className="staff-card staff-request-list">
              <div className="staff-card-head"><div><span className="staff-eyebrow">YOUR HISTORY</span><h3>Recent requests</h3></div></div>
              {requests.length ? visibleRequests.map((request) => {
                const status = String(request.status).toLowerCase();
                return <div className="staff-request-row" key={request.id || request.created_at}>
                  <span className="staff-request-icon"><FileText size={16}/></span>
                  <div>
                    <b>{requestLabel(request)}</b>
                    <small>{requestSchedule(request)}</small>
                    {request.reason && <small>{request.reason}</small>}
                    {status === "rejected" && request.decision_reason && <small className="staff-request-decision-reason"><strong>Rejection reason:</strong> {request.decision_reason}</small>}
                    {["approved", "rejected"].includes(status) && request.reviewed_by_name && <small className="staff-request-reviewer"><strong>{status === "approved" ? "Approved" : "Rejected"} by:</strong> {request.reviewed_by_name}{request.reviewed_by_designation ? ` (${request.reviewed_by_designation.replace(/_/g, "-")})` : ""}</small>}
                  </div>
                  <span className={`staff-status ${status}`}>{request.status}</span>
                </div>;
              }) : <div className="staff-empty"><FileText/><b>No requests submitted</b><span>Your submitted requests will be listed here.</span></div>}
              {requests.length > requestPageSize && <footer className="staff-request-pagination"><span>{visibleRequestPage * requestPageSize + 1}–{Math.min((visibleRequestPage + 1) * requestPageSize, requests.length)} of {requests.length}</span><div><button type="button" disabled={visibleRequestPage === 0} aria-label="Previous requests page" onClick={() => setRequestPage((current) => Math.max(0, current - 1))}>‹</button><span>{visibleRequestPage + 1}</span><button type="button" disabled={visibleRequestPage + 1 >= requestPageCount} aria-label="Next requests page" onClick={() => setRequestPage((current) => Math.min(requestPageCount - 1, current + 1))}>›</button></div></footer>}
            </article>
          </section>
        )}
        {section === "My profile" && <section className="staff-card staff-profile-card">
          <div className="staff-profile-cover"/>
          <div className="staff-profile-main">
            <div className="staff-profile-identity">
              <div className="staff-profile-photo-area" ref={profilePhotoAreaRef}>
                <button className="staff-profile-avatar" type="button" aria-label="Profile photo options" aria-expanded={profilePhotoMenuOpen} onClick={() => setProfilePhotoMenuOpen((open) => !open)}>{profile?.photo_url ? <img src={profile.photo_url} alt=""/> : <span>{initials || "TM"}</span>}<span className="staff-profile-avatar-camera"><Camera size={13}/></span></button>
                <input ref={profilePhotoInputRef} type="file" accept="image/*" hidden onChange={handleProfilePhotoChange}/>
                {profilePhotoMenuOpen && <div className="staff-profile-photo-menu" role="menu">{profile?.photo_url && <button type="button" role="menuitem" onClick={() => { setProfilePhotoMenuOpen(false); setProfilePhotoPreviewOpen(true); }}><Eye size={15}/>View photo</button>}<button type="button" role="menuitem" disabled={profilePhotoBusy} onClick={() => { setProfilePhotoMenuOpen(false); profilePhotoInputRef.current?.click(); }}><Camera size={15}/>{profilePhotoBusy ? "Uploading..." : profile?.photo_url ? "Change photo" : "Add photo"}</button></div>}
              </div>
              <div className="staff-profile-title"><h2>{displayName}</h2><p>{profile?.designation || "Team member"}{profile?.department ? ` · ${profile.department}` : ""}</p></div>
            </div>
            <div className="staff-profile-tabs" role="tablist" aria-label="Profile information">
              {profileGroups.map((group) => <button key={group.id} id={`staff-profile-tab-${group.id}`} type="button" role="tab" aria-selected={profileTab === group.id} aria-controls="staff-profile-panel" className={profileTab === group.id ? "active" : ""} onClick={() => setProfileTab(group.id)}>{group.label}</button>)}
            </div>
            <section className="staff-profile-tabpanel" id="staff-profile-panel" role="tabpanel" aria-labelledby={`staff-profile-tab-${activeProfileGroup.id}`}>
              <div className="staff-profile-fields">{activeProfileGroup.fields.map(([label, value]) => <div key={label}><span>{label}</span><b>{value === undefined || value === null || String(value).trim() === "" ? "Not provided" : String(value)}</b></div>)}</div>
            </section>
            <div className="staff-profile-note"><ShieldCheck size={17}/> Employment details are managed by your HR team.</div>
            
            <ProfileDataTables 
              employeeData={profile} 
              showMobileFooter={false} 
              token={token} 
              onDataChange={() => {
                console.log('Profile data change triggered');
                window.location.reload();
              }} 
              isEditable={true} 
            />
          </div>
        </section>}
      </div>
      <nav className="mobile-bottom-nav staff-mobile-bottom-nav" aria-label="Staff navigation">
        {[...navItems, [UserRound, "My profile"]].map(([Icon, label]) => (
          <button
            key={label}
            type="button"
            className={`mobile-bottom-item ${section === label ? "selected" : ""}`}
            onClick={() => go(label)}
            aria-current={section === label ? "page" : undefined}
          >
            <Icon size={16} />
            <span>{label === "My profile" ? "Profile" : label}</span>
          </button>
        ))}
      </nav>
      {passwordModalOpen && <div className="staff-modal-backdrop staff-password-backdrop" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget && !passwordBusy) closePasswordModal(); }}>
        <form className="settings-password-modal staff-password-dialog" onSubmit={submitPasswordChange}>
          <div className="settings-password-heading">
            <div><span className="staff-eyebrow">ACCOUNT SECURITY</span><h2>Change password</h2><p>Enter your current password and choose a new one.</p></div>
            <button type="button" className="staff-modal-x" onClick={closePasswordModal} disabled={passwordBusy} aria-label="Close password form"><X size={18}/></button>
          </div>
          <label className="settings-password-field">Current password<input type="password" autoComplete="current-password" value={passwordCurrent} onChange={(event) => setPasswordCurrent(event.target.value)} required /></label>
          <label className="settings-password-field">New password<input type="password" autoComplete="new-password" minLength={8} value={passwordNew} onChange={(event) => setPasswordNew(event.target.value)} required /></label>
          <label className="settings-password-field">Confirm new password<input type="password" autoComplete="new-password" minLength={8} value={passwordConfirm} onChange={(event) => setPasswordConfirm(event.target.value)} required /></label>
          {(passwordError || passwordMessage) && <p className={`staff-feedback ${passwordError ? "error" : "success"}`} role="status">{passwordError || passwordMessage}</p>}
          <div className="settings-password-actions"><button type="button" className="outline-action" onClick={closePasswordModal} disabled={passwordBusy}>Cancel</button><button className="primary-action" disabled={passwordBusy}>{passwordBusy ? "Updating…" : "Update password"}</button></div>
        </form>
      </div>}
      {shiftDialogOpen && <div className="staff-modal-backdrop" role="presentation"><section className="staff-shift-dialog" role="dialog" aria-modal="true" aria-labelledby="staff-shift-title"><button className="staff-modal-x" onClick={() => setShiftDialogOpen(false)} aria-label="Close"><X size={18}/></button><span className="staff-dialog-icon"><BriefcaseBusiness size={20}/></span><span className="staff-eyebrow">ATTENDANCE</span><h2 id="staff-shift-title">{attendanceType === "in" ? "Select your branch and shift" : "Select your attendance branch"}</h2><p>{attendanceType === "in" ? "Choose the branch and shift you’re starting at." : "Choose the branch where you are checking out."}</p>{attendanceBranches.length > 1 && <StaffAttendanceSelect label="Branch" value={attendanceBranchId} onChange={setAttendanceBranchId} placeholder="Choose a branch" options={attendanceBranches.map((branch) => ({ value: branch.branch_id, label: `${branch.name}${branch.is_default ? " (Default)" : ""}` }))} />}{attendanceType === "in" && <StaffAttendanceSelect label="Work shift" value={shiftId} onChange={setShiftId} placeholder="Choose a shift" options={(Array.isArray(shifts) ? shifts : []).map((shift) => ({ value: shift.shift_id, label: `${shift.name} (${String(shift.start_time).slice(0,5)} - ${String(shift.end_time).slice(0,5)})` }))} />}{attendanceType === "in" && attendanceBranchId && shifts.length === 0 && <p className="staff-dialog-error">No active shifts are available for this branch.</p>}{error && <p className="staff-dialog-error">{error}</p>}<div className="staff-dialog-actions"><button className="staff-button secondary" onClick={() => setShiftDialogOpen(false)}>Cancel</button><button className="staff-button primary" onClick={continueToCamera} disabled={!attendanceBranchId || (attendanceType === "in" && !shiftId)}>Continue to camera <ChevronRight size={15}/></button></div></section></div>}
      {cameraOpen && <div className="staff-modal-backdrop" role="presentation"><section className="staff-camera-dialog" role="dialog" aria-modal="true" aria-labelledby="staff-camera-title"><div className="staff-camera-head"><div><span className="staff-eyebrow">ATTENDANCE VERIFICATION</span><h2 id="staff-camera-title">Take selfie</h2></div><button className="staff-modal-x" onClick={closeCamera} aria-label="Close camera"><X size={18}/></button></div><video ref={videoRef} autoPlay playsInline muted/><canvas ref={canvasRef} hidden/><div className="staff-dialog-actions"><button className="staff-button secondary" onClick={closeCamera}>Cancel</button><button className="staff-button primary" onClick={captureSelfie}><Camera size={16}/> Capture selfie</button></div></section></div>}
      {!!selfiePreview && <div className="staff-modal-backdrop" role="presentation"><section className="staff-camera-dialog staff-preview-dialog" role="dialog" aria-modal="true" aria-labelledby="staff-preview-title"><div className="staff-camera-head"><div><span className="staff-eyebrow">ATTENDANCE VERIFICATION</span><h2 id="staff-preview-title">Review selfie</h2></div><button className="staff-modal-x" onClick={() => { URL.revokeObjectURL(selfiePreview); setSelfie(null); setSelfiePreview(""); }} aria-label="Close preview"><X size={18}/></button></div><img className="staff-selfie-preview" src={selfiePreview} alt="Captured attendance selfie"/><div className="staff-dialog-actions"><button className="staff-button secondary" onClick={() => { URL.revokeObjectURL(selfiePreview); setSelfie(null); setSelfiePreview(""); openCamera(); }}>Retake</button><button className="staff-button primary" disabled={busy} onClick={submitAttendance}>{busy ? "Getting location…" : `Submit check-${attendanceType === "in" ? "in" : "out"}`} <CheckCircle2 size={16}/></button></div></section></div>}
      {profilePhotoPreviewOpen && profile?.photo_url && <div className="staff-profile-photo-backdrop" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget) setProfilePhotoPreviewOpen(false); }}><section className="staff-profile-photo-preview" role="dialog" aria-modal="true" aria-label={`${displayName} profile photo`}><button className="staff-profile-photo-close" type="button" onClick={() => setProfilePhotoPreviewOpen(false)} aria-label="Close photo"><X size={18}/></button><img src={profile.photo_url} alt={`${displayName} profile`}/></section></div>}
    </main>
  </div>;
}
